/**
 * 角色详情：版本历史 + 完整设定 + 外观资产。
 *
 * 这里能看到角色宪章落到数据库里的样子：systemPrompt、10 段任务 prompt、
 * 双形态配置、兜底台词、受限表情枚举、Skill 授权。
 */

import {
  Alert,
  App,
  Button,
  Card,
  Collapse,
  Descriptions,
  Image,
  Input,
  InputNumber,
  Space,
  Table,
  Tabs,
  Tag,
  Typography,
  Upload,
} from "antd";
import { SaveOutlined, UploadOutlined } from "@ant-design/icons";
import { useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useParams } from "react-router-dom";
import { api } from "../../services/api";
import { useAuth } from "../../services/auth";
import { fmtBytes, fmtDateTime, fmtNumber, StatusTag, Tags } from "../../components/format";
import { SongLibraryPanel } from "./SongLibraryPanel";

interface CharacterDetail {
  id: string;
  key: string;
  displayName: string;
  status: string;
  publishedVersionId: string | null;
  counts: Record<string, number>;
  versions: {
    id: string;
    version: number;
    status: string;
    policyVersion: number;
    emotionKeys: string[];
    enabledSkillKeys: string[];
    createdBy: string | null;
    createdAt: string | null;
  }[];
  appearances: {
    id: string;
    kind: string;
    outfitKey: string | null;
    emotion: string | null;
    formKey: string | null;
    displayName: string | null;
    visibility: string;
    sourceKind: string;
    status: string;
    asset: { id: string; storageKey: string; status: string; mimeType: string; sizeBytes: number; legacyUrl: string | null };
  }[];
}

interface VersionDetail {
  id: string;
  version: number;
  status: string;
  systemPrompt: string;
  taskPrompts: Record<string, string>;
  fallbackLines: Record<string, string[]>;
  personaConfig: unknown;
  forms: Record<string, Record<string, unknown>>;
  emotionKeys: string[];
  enabledSkillKeys: string[];
  fewShots: unknown;
  policyVersion: number;
  createdBy: string | null;
  createdAt: string | null;
}

interface VoiceSettings {
  voiceId: string;
  model: string;
  pitchRate: number;
  speechRate: number;
}

type ReferenceView = "front" | "three_quarter_left" | "profile_left";

interface ReferenceSlot {
  view: ReferenceView;
  label: string;
  assetId: string;
  status: string;
  mimeType: string;
  sizeBytes: number;
  previewUrl: string | null;
}

interface CharacterReferences {
  characterId: string;
  characterName: string;
  publishedVersionId: string | null;
  version: number | null;
  uploadEnabled: boolean;
  maxBytes: number;
  slots: Record<ReferenceView, ReferenceSlot | null>;
}

const REFERENCE_SLOTS: {
  view: ReferenceView;
  fieldName: string;
  title: string;
  guidance: string;
}[] = [
  {
    view: "front",
    fieldName: "front",
    title: "正面（0°）",
    guidance: "脸和身体正对镜头，五官与服装结构完整可见",
  },
  {
    view: "three_quarter_left",
    fieldName: "threeQuarterLeft",
    title: "半侧（左前 45°）",
    guidance: "向角色左侧转约 45°，同时保留双眼和脸部体积",
  },
  {
    view: "profile_left",
    fieldName: "profileLeft",
    title: "侧面（左侧 90°）",
    guidance: "标准左侧面，清楚展示鼻尖、下巴、耳朵与发型轮廓",
  },
];

function AssetImagePreview({
  file,
  currentUrl,
  assetId,
  status,
  revision,
  configured,
  canRead,
  alt,
  height = 260,
}: {
  file?: File;
  currentUrl: string | null;
  assetId?: string;
  status?: string;
  revision?: string;
  configured: boolean;
  canRead: boolean;
  alt: string;
  height?: number;
}) {
  const [localUrl, setLocalUrl] = useState<string | null>(null);
  const [loadFailed, setLoadFailed] = useState(false);

  useEffect(() => {
    setLoadFailed(false);
    if (file) {
      const url = URL.createObjectURL(file);
      setLocalUrl(url);
      return () => URL.revokeObjectURL(url);
    }
    if (currentUrl || !assetId || status !== "ready" || !canRead) {
      setLocalUrl(null);
      return;
    }

    let active = true;
    let objectUrl: string | null = null;
    api
      .blob(`/api/v1/admin/assets/${assetId}/content`)
      .then((blob) => {
        if (!active) return;
        objectUrl = URL.createObjectURL(blob);
        setLocalUrl(objectUrl);
      })
      .catch(() => {
        if (active) setLoadFailed(true);
      });
    return () => {
      active = false;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [assetId, canRead, currentUrl, file, revision, status]);

  const src = localUrl ?? currentUrl;
  return src ? (
    <Image
      src={src}
      alt={alt}
      width="100%"
      height={height}
      style={{ objectFit: "contain", background: "#fafafa" }}
    />
  ) : (
    <div
      style={{
        height,
        display: "grid",
        placeItems: "center",
        background: "#fafafa",
        color: "#999",
        border: "1px dashed #d9d9d9",
      }}
    >
      {loadFailed
        ? "图片读取失败"
        : configured && status === "ready" && !canRead
          ? "缺少 asset.read 权限"
          : configured && status === "ready"
            ? "正在读取图片…"
            : configured
              ? "文件尚未上传"
              : "尚未上传"}
    </div>
  );
}

type Appearance = CharacterDetail["appearances"][number];
type AppearanceAsset = Appearance["asset"];

function voiceSettings(personaConfig: unknown): VoiceSettings {
  const persona =
    typeof personaConfig === "object" && personaConfig !== null && !Array.isArray(personaConfig)
      ? (personaConfig as Record<string, unknown>)
      : {};
  const voice =
    typeof persona["voice"] === "object" && persona["voice"] !== null && !Array.isArray(persona["voice"])
      ? (persona["voice"] as Record<string, unknown>)
      : {};
  return {
    voiceId: typeof voice["voiceId"] === "string" ? voice["voiceId"] : "",
    model: typeof voice["model"] === "string" ? voice["model"] : "",
    pitchRate: typeof voice["pitchRate"] === "number" ? voice["pitchRate"] : 1,
    speechRate: typeof voice["speechRate"] === "number" ? voice["speechRate"] : 1,
  };
}

function sameTextMap(left: Record<string, string>, right: Record<string, string>): boolean {
  const keys = Object.keys(left);
  return (
    keys.length === Object.keys(right).length &&
    keys.every((key) => Object.prototype.hasOwnProperty.call(right, key) && left[key] === right[key])
  );
}

function CharacterVersionEditor({
  characterId,
  version,
  isPublished,
  onPublished,
}: {
  characterId: string;
  version: VersionDetail | null | undefined;
  isPublished: boolean;
  onPublished: (versionId: string) => void;
}) {
  const { message } = App.useApp();
  const { can } = useAuth();
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [systemPrompt, setSystemPrompt] = useState("");
  const [taskPrompts, setTaskPrompts] = useState<Record<string, string>>({});
  const [pitchRate, setPitchRate] = useState(1);
  const [speechRate, setSpeechRate] = useState(1);
  const [reason, setReason] = useState("");

  useEffect(() => {
    setEditing(false);
    setReason("");
  }, [version?.id]);

  const voice = voiceSettings(version?.personaConfig);
  const canPublish = can("character.write") && can("character.publish");

  const beginEditing = () => {
    if (!version) return;
    setSystemPrompt(version.systemPrompt);
    setTaskPrompts({ ...version.taskPrompts });
    setPitchRate(voice.pitchRate);
    setSpeechRate(voice.speechRate);
    setReason("");
    setEditing(true);
  };

  const save = async () => {
    if (!version || saving) return;
    if (!reason.trim()) {
      message.warning("请填写修改原因，方便之后追溯版本");
      return;
    }
    if (!systemPrompt.trim()) {
      message.warning("System Prompt 不能为空");
      return;
    }
    const emptyTask = Object.entries(taskPrompts).find(([, text]) => !text.trim());
    if (emptyTask) {
      message.warning(`${emptyTask[0]} 的 Prompt 不能为空`);
      return;
    }

    const nextVoice = { pitchRate, speechRate };
    const systemChanged = systemPrompt !== version.systemPrompt;
    const tasksChanged = !sameTextMap(version.taskPrompts, taskPrompts);
    const voiceChanged = pitchRate !== voice.pitchRate || speechRate !== voice.speechRate;
    if (!systemChanged && !tasksChanged && !voiceChanged) {
      message.info("没有检测到设定变化");
      return;
    }

    setSaving(true);
    try {
      const result = await api.patch<{
        publishedVersionId: string;
        version: number;
        changedFields: string[];
      }>(`/api/v1/admin/characters/${characterId}/published-version`, {
        baseVersionId: version.id,
        ...(systemChanged ? { systemPrompt } : {}),
        ...(tasksChanged ? { taskPrompts } : {}),
        ...(voiceChanged ? { voice: nextVoice } : {}),
        reason: reason.trim(),
      });
      await queryClient.invalidateQueries({ queryKey: ["character", characterId] });
      setEditing(false);
      setReason("");
      onPublished(result.publishedVersionId);
      message.success(`角色设定 v${result.version} 已发布`);
    } catch (error) {
      message.error((error as Error).message || "角色设定保存失败");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card
      size="small"
      title="Prompt 与声音"
      extra={
        !editing && isPublished && canPublish ? (
          <Button type="primary" icon={<SaveOutlined />} onClick={beginEditing} disabled={!version}>
            编辑当前版本
          </Button>
        ) : null
      }
    >
      {!version ? (
        <Typography.Text type="secondary">请选择一个角色版本</Typography.Text>
      ) : (
        <Space direction="vertical" size={12} style={{ width: "100%" }}>
          {!isPublished && (
            <Alert
              type="info"
              showIcon
              message={`正在查看历史版本 v${version.version}`}
              description="历史快照不可原地编辑。选择带“已发布”标记的版本后可以修改。"
            />
          )}
          {isPublished && !canPublish && (
            <Alert
              type="info"
              showIcon
              message="当前账号只能查看角色设定"
              description="修改后会立即发布新版本，因此需要 character.write 与 character.publish 两项权限。"
            />
          )}
          <Descriptions size="small" column={{ xs: 1, sm: 2, lg: 4 }} bordered>
            <Descriptions.Item label="当前语速">
              <Typography.Text strong>{voice.speechRate.toFixed(2)}×</Typography.Text>
              {voice.speechRate === 1 ? "（原速）" : null}
            </Descriptions.Item>
            <Descriptions.Item label="语调">{voice.pitchRate.toFixed(2)}×</Descriptions.Item>
            <Descriptions.Item label="TTS 模型">
              <Typography.Text code>{voice.model || "未配置"}</Typography.Text>
            </Descriptions.Item>
            <Descriptions.Item label="复刻音色">
              <Typography.Text className="mono" ellipsis title={voice.voiceId}>
                {voice.voiceId || "未配置"}
              </Typography.Text>
            </Descriptions.Item>
          </Descriptions>

          {editing && (
            <Space direction="vertical" size={12} style={{ width: "100%" }}>
              <Alert
                type="warning"
                showIcon
                message="保存后立即生效"
                description="系统会新建一个不可变版本并保留当前版本，不会覆盖历史记录。已经合成过的消息音频不会变化；之后首次合成的消息使用新语速。"
              />
              <Card size="small" title="声音参数">
                <Space wrap size={24}>
                  <Space direction="vertical" size={4}>
                    <Typography.Text>语速倍率</Typography.Text>
                    <InputNumber
                      min={0.5}
                      max={2}
                      step={0.05}
                      precision={2}
                      value={speechRate}
                      onChange={(value) => setSpeechRate(value ?? 1)}
                      addonAfter="×"
                    />
                    <Typography.Text type="secondary">0.5 最慢，1.0 原速，2.0 最快</Typography.Text>
                  </Space>
                  <Space direction="vertical" size={4}>
                    <Typography.Text>语调倍率</Typography.Text>
                    <InputNumber
                      min={0.5}
                      max={2}
                      step={0.05}
                      precision={2}
                      value={pitchRate}
                      onChange={(value) => setPitchRate(value ?? 1)}
                      addonAfter="×"
                    />
                    <Typography.Text type="secondary">1.0 为复刻音色的原始语调</Typography.Text>
                  </Space>
                </Space>
              </Card>
              <Card size="small" title={`System Prompt（${systemPrompt.length} 字）`}>
                <Input.TextArea
                  value={systemPrompt}
                  onChange={(event) => setSystemPrompt(event.target.value)}
                  autoSize={{ minRows: 12, maxRows: 28 }}
                  className="mono"
                  disabled={saving}
                />
              </Card>
              <Collapse
                size="small"
                items={Object.entries(taskPrompts).map(([key, text]) => ({
                  key,
                  label: `${key}（${text.length} 字）`,
                  children: (
                    <Input.TextArea
                      value={text}
                      onChange={(event) =>
                        setTaskPrompts((current) => ({ ...current, [key]: event.target.value }))
                      }
                      autoSize={{ minRows: 8, maxRows: 24 }}
                      className="mono"
                      disabled={saving}
                    />
                  ),
                }))}
              />
              <Input.TextArea
                value={reason}
                onChange={(event) => setReason(event.target.value)}
                placeholder="必填：本次修改原因，例如：将缈缈朗读语速调整为 0.90×"
                maxLength={200}
                showCount
                autoSize={{ minRows: 2, maxRows: 4 }}
                disabled={saving}
              />
              <Space>
                <Button type="primary" icon={<SaveOutlined />} loading={saving} onClick={save}>
                  保存并发布新版本
                </Button>
                <Button onClick={() => setEditing(false)} disabled={saving}>
                  取消
                </Button>
              </Space>
            </Space>
          )}
        </Space>
      )}
    </Card>
  );
}

function ReplaceAppearanceFileButton({
  characterId,
  asset,
  enabled,
}: {
  characterId: string;
  asset: AppearanceAsset;
  enabled: boolean;
}) {
  const { message } = App.useApp();
  const queryClient = useQueryClient();
  const [busy, setBusy] = useState(false);

  return (
    <Upload
      showUploadList={false}
      accept="image/png,image/jpeg,image/webp,image/gif,image/svg+xml"
      disabled={!enabled || busy}
      customRequest={async ({ file, onError, onSuccess }) => {
        const form = new FormData();
        form.append("file", file as File);
        setBusy(true);
        try {
          const result = await api.postForm<{
            id: string;
            storageKey: string;
            status: string;
          }>(`/api/v1/admin/assets/${asset.id}/file`, form);
          await Promise.all([
            queryClient.invalidateQueries({ queryKey: ["character", characterId] }),
            queryClient.invalidateQueries({
              predicate: (query) => query.queryKey[0] === "/api/v1/admin/assets",
            }),
          ]);
          message.success(asset.status === "ready" ? "外观图片已替换" : "外观图片已上传");
          onSuccess?.(result);
        } catch (error) {
          message.error((error as Error).message || "上传失败");
          onError?.(error as Error);
        } finally {
          setBusy(false);
        }
      }}
    >
      <Button size="small" icon={<UploadOutlined />} loading={busy} disabled={!enabled || busy}>
        {asset.status === "ready" ? "替换图片" : "上传图片"}
      </Button>
    </Upload>
  );
}

function AppearanceAssetsPanel({
  characterId,
  appearances,
}: {
  characterId: string;
  appearances: Appearance[];
}) {
  const { can } = useAuth();
  const canReadAssets = can("asset.read");
  const canWriteAssets = can("asset.write");
  const storage = useQuery({
    queryKey: ["assetStorage"],
    queryFn: () =>
      api.get<{
        uploadEnabled: boolean;
        maxBytes: number;
        missing: string[];
      }>("/api/v1/admin/assets/storage"),
    enabled: canReadAssets,
  });
  const canUpload = canWriteAssets && storage.data?.uploadEnabled === true;

  return (
    <Space direction="vertical" size={12} style={{ width: "100%" }}>
      {canWriteAssets && storage.data && !storage.data.uploadEnabled && (
        <Alert
          type="warning"
          showIcon
          message="现在还不能上传外观图片"
          description={`对象存储配置缺少：${storage.data.missing.join("、")}`}
        />
      )}
      <Card styles={{ body: { padding: 12 } }}>
        <Typography.Paragraph type="secondary" style={{ fontSize: 12 }}>
          这里管理聊天界面使用的头像、立绘和场景图。预览通过管理员鉴权从 OSS
          回源，不要求配置公共图片域名；替换时保留 Asset ID，因此所有引用会自动生效。
        </Typography.Paragraph>
        {!canReadAssets && (
          <Alert
            style={{ marginBottom: 12 }}
            type="warning"
            showIcon
            message="当前账号缺少 asset.read 权限，无法读取图片文件"
          />
        )}
        <Table<Appearance>
          size="small"
          rowKey="id"
          pagination={false}
          dataSource={appearances}
          scroll={{ x: 1120 }}
          columns={[
            {
              title: "预览",
              dataIndex: "asset",
              width: 110,
              fixed: "left",
              render: (asset: AppearanceAsset, row) => (
                <div style={{ width: 86 }}>
                  <AssetImagePreview
                    currentUrl={asset.legacyUrl}
                    assetId={asset.id}
                    status={asset.status}
                    revision={asset.storageKey}
                    configured
                    canRead={canReadAssets}
                    alt={row.displayName ?? `${row.formKey ?? ""} ${row.emotion ?? row.kind}`}
                    height={96}
                  />
                </div>
              ),
            },
            { title: "类型", dataIndex: "kind", width: 170 },
            { title: "形态", dataIndex: "formKey", width: 110, render: (val) => <StatusTag value={val} /> },
            { title: "表情", dataIndex: "emotion", width: 100, render: (val) => val || "—" },
            { title: "装扮", dataIndex: "outfitKey", width: 100, render: (val) => val || "—" },
            {
              title: "资产路径",
              dataIndex: "asset",
              width: 260,
              render: (asset: AppearanceAsset) => (
                <Typography.Text className="mono" ellipsis title={asset.storageKey}>
                  {asset.storageKey}
                </Typography.Text>
              ),
            },
            {
              title: "状态",
              dataIndex: "asset",
              width: 120,
              render: (asset: AppearanceAsset) => <StatusTag value={asset.status} />,
            },
            {
              title: "大小",
              dataIndex: "asset",
              width: 90,
              align: "right",
              render: (asset: AppearanceAsset) => fmtBytes(asset.sizeBytes),
            },
            { title: "来源", dataIndex: "sourceKind", width: 110 },
            { title: "可见性", dataIndex: "visibility", width: 100, render: (val) => <StatusTag value={val} /> },
            {
              title: "操作",
              dataIndex: "asset",
              width: 120,
              fixed: "right",
              render: (asset: AppearanceAsset) =>
                canWriteAssets ? (
                  <ReplaceAppearanceFileButton
                    characterId={characterId}
                    asset={asset}
                    enabled={canUpload}
                  />
                ) : (
                  <Typography.Text type="secondary">只读</Typography.Text>
                ),
            },
          ]}
        />
      </Card>
    </Space>
  );
}

function CharacterReferencePanel({
  characterId,
  onPublished,
}: {
  characterId: string;
  onPublished: (versionId: string) => void;
}) {
  const { message } = App.useApp();
  const { can } = useAuth();
  const queryClient = useQueryClient();
  const [selected, setSelected] = useState<Partial<Record<ReferenceView, File>>>({});
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);

  const references = useQuery({
    queryKey: ["characterReferences", characterId],
    queryFn: () =>
      api.get<CharacterReferences>(
        `/api/v1/admin/characters/${characterId}/image-generation/references`,
      ),
    enabled: !!characterId,
  });

  const editable = can("character.write");
  const canReadAssets = can("asset.read");
  const changedViews = Object.keys(selected) as ReferenceView[];
  const missingViews = REFERENCE_SLOTS.filter(
    ({ view }) => !references.data?.slots[view] && !selected[view],
  );

  const removeSelection = (view: ReferenceView) => {
    setSelected((previous) => {
      const next = { ...previous };
      delete next[view];
      return next;
    });
  };

  const save = async () => {
    if (changedViews.length === 0) {
      message.warning("请至少选择一张要上传的参考图");
      return;
    }
    if (missingViews.length) {
      message.warning(`首次保存还缺：${missingViews.map((slot) => slot.title).join("、")}`);
      return;
    }
    if (!reason.trim()) {
      message.warning("请填写本次更新原因");
      return;
    }

    const form = new FormData();
    for (const slot of REFERENCE_SLOTS) {
      const file = selected[slot.view];
      if (file) form.append(slot.fieldName, file);
    }
    form.append("reason", reason.trim());

    setSaving(true);
    try {
      const result = await api.postForm<{
        publishedVersionId: string;
        version: number;
      }>(
        `/api/v1/admin/characters/${characterId}/image-generation/references`,
        form,
      );
      setSelected({});
      setReason("");
      onPublished(result.publishedVersionId);
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["character", characterId] }),
        queryClient.invalidateQueries({ queryKey: ["characterReferences", characterId] }),
        queryClient.invalidateQueries({ queryKey: ["characterVersion"] }),
      ]);
      message.success(`三视图已保存，角色资料 v${result.version} 已发布`);
    } catch (error) {
      message.error((error as Error).message || "保存失败");
    } finally {
      setSaving(false);
    }
  };

  if (references.error) {
    return <Alert type="error" showIcon message={(references.error as Error).message} />;
  }

  return (
    <Space direction="vertical" size={12} style={{ width: "100%" }}>
      <Alert
        type="info"
        showIcon
        message="这三张图用于让生图模型认准角色，不是聊天页的头像或立绘"
        description="首次保存必须一次凑齐三张；以后可以只替换其中一张。保存会自动发布一个新角色资料版本，旧图和旧版本仍会保留。建议三张图使用同一画风、同一服装、同一发型、同一画布比例和接近的光照。"
      />
      {references.data && !references.data.uploadEnabled && editable && (
        <Alert
          type="warning"
          showIcon
          message="现在还不能上传图片"
          description="后端尚未连接阿里云 OSS。请先配置 OSS_BUCKET、OSS_ACCESS_KEY_ID、OSS_ACCESS_KEY_SECRET，再重启 API。"
        />
      )}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(230px, 1fr))",
          gap: 12,
        }}
      >
        {REFERENCE_SLOTS.map((slot) => {
          const current = references.data?.slots[slot.view] ?? null;
          const file = selected[slot.view];
          return (
            <Card
              key={slot.view}
              size="small"
              title={slot.title}
              loading={references.isLoading}
              extra={current && !file ? <Tag color="green">已配置</Tag> : file ? <Tag color="blue">待保存</Tag> : null}
            >
              <Space direction="vertical" size={8} style={{ width: "100%" }}>
                <AssetImagePreview
                  file={file}
                  currentUrl={current?.previewUrl ?? null}
                  assetId={current?.assetId}
                  status={current?.status}
                  configured={Boolean(current)}
                  canRead={canReadAssets}
                  alt={slot.title}
                />
                <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                  {slot.guidance}
                </Typography.Text>
                {file ? (
                  <Typography.Text ellipsis title={file.name}>
                    已选择：{file.name}（{fmtBytes(file.size)}）
                  </Typography.Text>
                ) : current ? (
                  <Typography.Text type="secondary">
                    当前文件：{current.mimeType} · {fmtBytes(current.sizeBytes)}
                  </Typography.Text>
                ) : null}
                {editable && (
                  <Space>
                    <Upload
                      accept="image/png,image/jpeg,image/webp"
                      showUploadList={false}
                      disabled={!references.data?.uploadEnabled || saving}
                      beforeUpload={(nextFile) => {
                        if (!(["image/png", "image/jpeg", "image/webp"] as string[]).includes(nextFile.type)) {
                          message.error("参考图仅支持 PNG、JPEG、WebP");
                          return Upload.LIST_IGNORE;
                        }
                        if (references.data && nextFile.size > references.data.maxBytes) {
                          message.error(`单张图片不能超过 ${fmtBytes(references.data.maxBytes)}`);
                          return Upload.LIST_IGNORE;
                        }
                        setSelected((previous) => ({ ...previous, [slot.view]: nextFile }));
                        return false;
                      }}
                    >
                      <Button icon={<UploadOutlined />} disabled={!references.data?.uploadEnabled || saving}>
                        {current || file ? "选择替换图" : "选择图片"}
                      </Button>
                    </Upload>
                    {file && (
                      <Button type="link" onClick={() => removeSelection(slot.view)} disabled={saving}>
                        取消选择
                      </Button>
                    )}
                  </Space>
                )}
              </Space>
            </Card>
          );
        })}
      </div>
      {editable && (
        <Card size="small" title="保存并发布">
          <Space direction="vertical" size={8} style={{ width: "100%" }}>
            <Input.TextArea
              value={reason}
              onChange={(event) => setReason(event.target.value)}
              placeholder="更新原因，例如：上传缈缈默认服装身份三视图"
              maxLength={200}
              showCount
              autoSize={{ minRows: 2, maxRows: 4 }}
              disabled={saving}
            />
            <Space wrap>
              <Button
                type="primary"
                icon={<SaveOutlined />}
                loading={saving}
                disabled={!references.data?.uploadEnabled || changedViews.length === 0}
                onClick={save}
              >
                保存并发布新版本
              </Button>
              <Typography.Text type="secondary">
                {changedViews.length
                  ? `本次将更新 ${changedViews.length} 张图`
                  : "选择图片后才会产生新版本"}
              </Typography.Text>
            </Space>
          </Space>
        </Card>
      )}
    </Space>
  );
}

export function CharacterDetailPage() {
  const { id = "" } = useParams();
  const [versionId, setVersionId] = useState<string | null>(null);

  const detail = useQuery({
    queryKey: ["character", id],
    queryFn: () => api.get<CharacterDetail | null>(`/api/v1/admin/characters/${id}`),
  });

  const activeVersionId = versionId ?? detail.data?.publishedVersionId ?? null;

  const version = useQuery({
    queryKey: ["characterVersion", activeVersionId],
    queryFn: () => api.get<VersionDetail | null>(`/api/v1/admin/characters/versions/${activeVersionId}`),
    enabled: !!activeVersionId,
  });

  if (detail.error) return <Alert type="error" showIcon message={(detail.error as Error).message} />;
  const c = detail.data;
  const v = version.data;

  return (
    <Space direction="vertical" size={12} style={{ width: "100%" }}>
      <Card title={c?.displayName ?? "角色"} loading={detail.isLoading} styles={{ body: { padding: 16 } }}>
        <Descriptions size="small" column={4} bordered>
          <Descriptions.Item label="key">{c?.key}</Descriptions.Item>
          <Descriptions.Item label="状态">
            <StatusTag value={c?.status} />
          </Descriptions.Item>
          <Descriptions.Item label="关系数">{fmtNumber(c?.counts?.["relationships"])}</Descriptions.Item>
          <Descriptions.Item label="日记数">{fmtNumber(c?.counts?.["diaries"])}</Descriptions.Item>
        </Descriptions>
      </Card>

      <Tabs
        items={[
          {
            key: "version",
            label: "角色设定",
            children: (
              <Space direction="vertical" size={12} style={{ width: "100%" }}>
                <Card size="small" title="版本历史" loading={detail.isLoading}>
                  <Table
                    size="small"
                    rowKey="id"
                    pagination={false}
                    dataSource={c?.versions ?? []}
                    rowClassName={(r) => (r.id === activeVersionId ? "ant-table-row-selected" : "")}
                    onRow={(r) => ({ onClick: () => setVersionId(r.id), style: { cursor: "pointer" } })}
                    columns={[
                      {
                        title: "版本",
                        dataIndex: "version",
                        width: 90,
                        render: (val: number, r) => (
                          <Space>
                            <span>v{val}</span>
                            {r.id === c?.publishedVersionId && <Tag color="green">已发布</Tag>}
                          </Space>
                        ),
                      },
                      { title: "状态", dataIndex: "status", width: 110, render: (val) => <StatusTag value={val} /> },
                      { title: "定价策略", dataIndex: "policyVersion", width: 100, render: (val) => `v${val}` },
                      {
                        title: "表情枚举",
                        dataIndex: "emotionKeys",
                        render: (val: string[]) => <Tags values={val} />,
                      },
                      {
                        title: "开放 Skill",
                        dataIndex: "enabledSkillKeys",
                        width: 110,
                        align: "right",
                        render: (val: string[]) => `${val.length} 个`,
                      },
                      { title: "操作者", dataIndex: "createdBy", width: 100, render: (val) => val || "—" },
                      { title: "时间", dataIndex: "createdAt", width: 170, render: fmtDateTime },
                    ]}
                  />
                </Card>

                <CharacterVersionEditor
                  characterId={id}
                  version={v}
                  isPublished={Boolean(activeVersionId && activeVersionId === c?.publishedVersionId)}
                  onPublished={(publishedVersionId) => setVersionId(publishedVersionId)}
                />

                <Card size="small" title={`v${v?.version ?? "?"} 完整设定`} loading={version.isLoading}>
                  <Collapse
                    size="small"
                    defaultActiveKey={["system"]}
                    items={[
                      {
                        key: "system",
                        label: `System Prompt（${v?.systemPrompt.length ?? 0} 字）`,
                        children: <div className="mono">{v?.systemPrompt}</div>,
                      },
                      {
                        key: "tasks",
                        label: `任务 Prompt（${Object.keys(v?.taskPrompts ?? {}).length} 段，按 capability key 分环节）`,
                        children: (
                          <Space direction="vertical" size={8} style={{ width: "100%" }}>
                            {Object.entries(v?.taskPrompts ?? {}).map(([k, text]) => (
                              <Card key={k} size="small" title={<Typography.Text code>{k}</Typography.Text>}>
                                <div className="mono">{String(text)}</div>
                              </Card>
                            ))}
                          </Space>
                        ),
                      },
                      {
                        key: "forms",
                        label: "形态配置（normal / weakened）",
                        children: (
                          <>
                            <Typography.Paragraph type="secondary" style={{ fontSize: 12 }}>
                              形态只引用 capability key，绝不出现模型 ID。每个角色必须有一个 isFallback
                              形态，否则灵力耗尽会出现硬墙。
                            </Typography.Paragraph>
                            <div className="mono">{JSON.stringify(v?.forms, null, 2)}</div>
                          </>
                        ),
                      },
                      {
                        key: "fallback",
                        label: "兜底台词",
                        children: (
                          <>
                            <Typography.Paragraph type="secondary" style={{ fontSize: 12 }}>
                              值是字符串数组，每次随机选一条避免机械重复。读取失败时回落代码常量，不允许白屏。
                            </Typography.Paragraph>
                            <div className="mono">{JSON.stringify(v?.fallbackLines, null, 2)}</div>
                          </>
                        ),
                      },
                      {
                        key: "persona",
                        label: "人设配置",
                        children: <div className="mono">{JSON.stringify(v?.personaConfig, null, 2)}</div>,
                      },
                      {
                        key: "skills",
                        label: `角色级 Skill 授权（${v?.enabledSkillKeys.length ?? 0} 个）`,
                        children: <Tags values={v?.enabledSkillKeys} />,
                      },
                      {
                        key: "fewshots",
                        label: "少样本示例",
                        children: <div className="mono">{JSON.stringify(v?.fewShots, null, 2)}</div>,
                      },
                    ]}
                  />
                </Card>
              </Space>
            ),
          },
          {
            key: "image-references",
            label: "生图参考图",
            children: (
              <CharacterReferencePanel
                characterId={id}
                onPublished={(publishedVersionId) => setVersionId(publishedVersionId)}
              />
            ),
          },
          {
            key: "songs",
            label: "音乐作品",
            children: <SongLibraryPanel characterId={id} />,
          },
          {
            key: "appearances",
            label: `外观资产 ${c?.appearances.length ?? 0}`,
            children: (
              <AppearanceAssetsPanel
                characterId={id}
                appearances={c?.appearances ?? []}
              />
            ),
          },
        ]}
      />
    </Space>
  );
}
